// src/components/admin/compensation/CompensationSetupGuide.jsx

import React, { useState } from 'react';
import { FiChevronDown, FiChevronRight, FiCheck, FiUser, FiDollarSign, FiTrendingUp, FiClock } from 'react-icons/fi';

export default function CompensationSetupGuide() {
  const [expandedStep, setExpandedStep] = useState(null);

  const toggleStep = (stepIndex) => {
    setExpandedStep(expandedStep === stepIndex ? null : stepIndex);
  };

  const setupSteps = [
    {
      title: "Enable Advanced Compensation Plans",
      icon: <FiCheck className="text-green-600" />,
      time: "1 minute",
      description: "Turn on the feature for your clinic",
      details: [
        "Toggle the 'Enable Advanced Compensation Plans' switch at the top",
        "System automatically creates default plans from your existing role rates",
        "Nothing changes for staff until you assign them specific plans",
        "You can disable this anytime to go back to simple role rates"
      ]
    },
    {
      title: "Create Your First Compensation Plan",
      icon: <FiDollarSign className="text-blue-600" />,
      time: "3 minutes",
      description: "Design a plan that fits your clinic's needs",
      details: [
        "Click 'Create New Plan' button",
        "Choose plan type:",
        "• Salary: Fixed monthly amount (e.g., $4,000/month)",
        "• Hourly: Pay per hour worked (e.g., $35/hour)", 
        "• Commission: Percentage of revenue (e.g., 20% of invoices)",
        "• Hybrid: Base salary + commission (e.g., $3,000 + 10%)",
        "Give it a clear name like 'Senior Clinician Plan'",
        "Add description explaining when to use this plan"
      ]
    },
    {
      title: "Assign Plans to Individual Staff",
      icon: <FiUser className="text-purple-600" />,
      time: "2 minutes per person",
      description: "Each staff member gets their own plan (or keeps old rate)",
      details: [
        "Go to 'Staff Compensation' tab",
        "Find the staff member you want to update",
        "Click 'Assign Plan' next to their name",
        "Choose which compensation plan fits their role",
        "Set effective date (can be immediate or future)",
        "Staff not assigned a plan keep using their old role rate",
        "You can mix and match - some on plans, some on old rates"
      ]
    },
    {
      title: "Test Run Your First Payroll",
      icon: <FiTrendingUp className="text-orange-600" />,
      time: "5 minutes",
      description: "See how the automatic calculations work",
      details: [
        "Let staff work normally for a few days",
        "System automatically tracks:",
        "• Hours worked (for hourly plans)",
        "• Invoices paid (for commission plans)", 
        "• Monthly salary amounts",
        "Go to Admin → Financial → Payroll",
        "Click 'Generate Payroll' to see calculations",
        "Review the breakdown before processing payments"
      ]
    }
  ];

  const planTypes = [
    {
      type: "Salary",
      icon: <FiClock className="text-green-600" />,
      description: "Fixed monthly amount",
      example: "$4,000/month",
      bestFor: "Full-time managers, senior staff",
      color: "bg-green-50 border-green-200"
    },
    {
      type: "Hourly", 
      icon: <FiClock className="text-blue-600" />,
      description: "Pay per hour worked",
      example: "$35/hour",
      bestFor: "Part-time staff, contractors",
      color: "bg-blue-50 border-blue-200"
    },
    {
      type: "Commission",
      icon: <FiTrendingUp className="text-purple-600" />,
      description: "Percentage of revenue generated",
      example: "20% of invoices",
      bestFor: "Sales roles, revenue generators",
      color: "bg-purple-50 border-purple-200"
    },
    {
      type: "Hybrid",
      icon: <FiDollarSign className="text-orange-600" />,
      description: "Base salary + commission bonus",
      example: "$3,000 + 10%",
      bestFor: "Senior clinicians, team leads",
      color: "bg-orange-50 border-orange-200"
    }
  ];

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          🚀 Quick Setup Guide - Get Started in 15 Minutes
        </h3>
        <p className="text-gray-600">
          Follow these simple steps to set up advanced compensation plans for your clinic.
          <span className="font-medium text-blue-600 ml-1">Each staff member gets their own individual plan.</span>
        </p>
      </div>

      {/* Setup Steps */}
      <div className="space-y-4 mb-8">
        {setupSteps.map((step, index) => (
          <div key={index} className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleStep(index)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  {step.icon}
                </div>
                <div className="text-left">
                  <h4 className="font-medium text-gray-900">
                    Step {index + 1}: {step.title}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {step.description} • {step.time}
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0">
                {expandedStep === index ? (
                  <FiChevronDown className="text-gray-400" />
                ) : (
                  <FiChevronRight className="text-gray-400" />
                )}
              </div>
            </button>
            
            {expandedStep === index && (
              <div className="px-4 pb-4">
                <ul className="space-y-2">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start space-x-2">
                      <div className="flex-shrink-0 w-1.5 h-1.5 bg-blue-600 rounded-full mt-2"></div>
                      <span className="text-sm text-gray-700">{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Plan Types Reference */}
      <div>
        <h4 className="font-medium text-gray-900 mb-4">📋 Compensation Plan Types</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {planTypes.map((plan, index) => (
            <div key={index} className={`p-4 rounded-lg border ${plan.color}`}>
              <div className="flex items-center space-x-2 mb-2">
                {plan.icon}
                <h5 className="font-medium text-gray-900">{plan.type}</h5>
              </div>
              <p className="text-sm text-gray-600 mb-1">{plan.description}</p>
              <p className="text-sm font-medium text-gray-900 mb-2">Example: {plan.example}</p>
              <p className="text-xs text-gray-500">Best for: {plan.bestFor}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Key Benefits */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">💡 Remember: Individual Assignment</h4>
        <p className="text-sm text-blue-800">
          You don't have to put everyone on compensation plans at once. Start with 1-2 key staff members, 
          see how it works, then gradually assign plans to others. Staff without assigned plans continue 
          using their existing role rates - nothing breaks!
        </p>
      </div>
    </div>
  );
}
